import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Folder as FolderIcon, FolderOpen, ChevronRight, ArchiveRestore, Search, ArrowRight, Plus, Trash2, GraduationCap } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";

const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
const slug = (s) => String(s).replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();

function Folder({ label, count, depth, open: initOpen = false, tid, actions, accent, children }) {
  const [open, setOpen] = useState(initOpen);
  useEffect(() => { setOpen(initOpen); }, [initOpen]);
  const Icon = open ? FolderOpen : FolderIcon;
  return (
    <div>
      <div style={{ paddingLeft: 16 + depth * 22 }} className="flex items-center gap-2 pr-4 border-b border-slate-100 hover:bg-slate-50 transition-colors">
        <button onClick={() => setOpen((o) => !o)} className="flex-1 flex items-center gap-2 py-2.5 text-left" data-testid={`ablage-folder-${tid}`}>
          <ChevronRight size={14} className={`text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
          <Icon size={depth === 0 ? 18 : 16} strokeWidth={1.6} className={accent || (depth === 0 ? "text-[#00807E]" : "text-amber-500")} />
          <span className={depth === 0 ? "font-semibold text-slate-900 text-base" : `text-sm ${count ? "text-slate-800" : "text-slate-400"}`}>{label}</span>
        </button>
        {actions}
        <span className="text-[11px] text-slate-500 tabular-nums w-6 text-right">{count}</span>
      </div>
      {open && children()}
    </div>
  );
}

function Row({ depth, to, name, meta, extra, tid }) {
  return (
    <div style={{ paddingLeft: 40 + depth * 22 }} className="flex flex-wrap items-center gap-3 py-2.5 pr-4 border-b border-slate-100 bg-white hover:bg-slate-50/70 transition-colors" data-testid={tid}>
      <Link to={to} className="text-sm font-medium text-slate-900 hover:underline min-w-[180px]">{name}</Link>
      <span className="text-xs text-slate-500">{meta}</span>
      <div className="ml-auto flex gap-2">
        {extra}
        <Link to={to} className="inline-flex items-center gap-1 text-[11px] bg-slate-900 text-white px-2 py-1 hover:bg-slate-700 transition-colors">Akte <ArrowRight size={12} /></Link>
      </div>
    </div>
  );
}

function ClientRow({ c, depth, ctx }) {
  return (
    <Row depth={depth} to={`/avgs/${c.client_id}`} name={c.name} tid={`ablage-client-${c.client_id}`}
      meta={`Coach: ${c.coach_names?.join(", ") || "—"} · ${formatDeShort(c.start_date)} – ${formatDeShort(c.end_date)} · abgelegt ${c.archived_at ? formatDeShort(c.archived_at.slice(0, 10)) : ""}${c.archived_by ? ` (${c.archived_by})` : ""}`}
      extra={ctx.canRestore && (
        <button onClick={() => ctx.restore(c)} className="inline-flex items-center gap-1 text-[11px] border border-slate-300 px-2 py-1 hover:border-slate-900 transition-colors" data-testid={`ablage-restore-${c.client_id}`}>
          <ArchiveRestore size={12} /> Zurückholen
        </button>
      )} />
  );
}

function Months({ clients, depth, tid, ctx }) {
  return MONTHS.map((m, i) => {
    const list = clients.filter((c) => Number(c.start_date?.slice(5, 7)) === i + 1);
    return (
      <Folder key={m} label={m} count={list.length} depth={depth} tid={`${tid}-${slug(m)}`} open={ctx.search && list.length > 0}>
        {() => list.map((c) => <ClientRow key={c.client_id} c={c} depth={depth + 1} ctx={ctx} />)}
      </Folder>
    );
  });
}

function MeasureFolder({ year, m, clients, ctx }) {
  const tid = `${year}-${slug(m.name)}`;
  const del = ctx.canManage && clients.length === 0 && (
    <button onClick={() => ctx.removeMeasure(year, m)} title="Ordner löschen" className="p-1 text-slate-400 hover:text-red-600 transition-colors" data-testid={`ablage-delete-${tid}`}><Trash2 size={13} /></button>
  );
  const loose = clients.filter((c) => !c.modules.some((x) => m.modules.includes(x)));
  return (
    <Folder label={`${m.name}${m.number ? ` · Nr. ${m.number}` : ""}`} count={clients.length} depth={1} tid={tid} actions={del} open={ctx.search && clients.length > 0}>
      {() => (m.modules.length === 0 ? <Months clients={clients} depth={2} tid={tid} ctx={ctx} /> : (
        <>
          {m.modules.map((mod) => {
            const list = clients.filter((c) => c.modules.includes(mod));
            return (
              <Folder key={mod} label={mod} count={list.length} depth={2} tid={`${tid}-${slug(mod)}`} open={ctx.search && list.length > 0}>
                {() => <Months clients={list} depth={3} tid={`${tid}-${slug(mod)}`} ctx={ctx} />}
              </Folder>
            );
          })}
          {loose.length > 0 && (
            <Folder label="Ohne Modul" count={loose.length} depth={2} tid={`${tid}-ohne-modul`}>
              {() => <Months clients={loose} depth={3} tid={`${tid}-ohne-modul`} ctx={ctx} />}
            </Folder>
          )}
        </>
      ))}
    </Folder>
  );
}

function WbFolder({ year, classes, ctx }) {
  const total = classes.reduce((s, c) => s + c.participants.length, 0);
  return (
    <Folder label="Weiterbildung (FbW)" count={total} depth={1} tid={`${year}-weiterbildung`} accent="text-sky-600" open={ctx.search && total > 0}>
      {() => classes.length === 0 ? <div className="pl-24 py-2.5 text-xs text-slate-400 border-b border-slate-100">Keine Klassen mit Start {year}</div>
        : classes.map((c) => (
          <Folder key={c.class_id} label={`${c.name} (${formatDeShort(c.start_date)} – ${formatDeShort(c.end_date)})`} count={c.participants.length} depth={2} tid={`wb-${c.class_id}`} accent="text-sky-500" open={ctx.search && c.participants.length > 0}>
            {() => c.participants.map((p) => (
              <Row key={p.participant_id} depth={3} to={`/weiterbildung/kunden/${p.participant_id}`} name={p.name} tid={`ablage-wb-${p.participant_id}`}
                meta={`${c.weiterbildung_name || "Weiterbildung"}${c.measure_number ? ` · Nr. ${c.measure_number}` : ""}`} />
            ))}
          </Folder>
        ))}
    </Folder>
  );
}

function AddMeasure({ year, ctx, existing }) {
  const opts = ctx.allMeasures.filter((m) => !existing.includes(m.measure_id));
  if (!ctx.canManage || opts.length === 0) return null;
  return (
    <label className="inline-flex items-center gap-1 text-[11px] border border-slate-300 px-2 py-1 hover:border-slate-900 transition-colors cursor-pointer" onClick={(e) => e.stopPropagation()}>
      <Plus size={12} /> Maßnahme
      <select value="" onChange={(e) => e.target.value && ctx.addMeasure(year, e.target.value)} className="bg-transparent outline-none text-[11px] w-4" data-testid={`ablage-add-measure-${year}`}>
        <option value="">–</option>
        {opts.map((m) => <option key={m.measure_id} value={m.measure_id}>{m.name}</option>)}
      </select>
    </label>
  );
}

function YearFolder({ y, clients, wb, ctx }) {
  const inYear = clients.filter((c) => c.start_date?.startsWith(String(y.year)));
  const ids = y.measures.map((m) => m.measure_id);
  const other = inYear.filter((c) => !ids.includes(c.measure_id));
  const wbCount = (wb || []).reduce((s, c) => s + c.participants.length, 0);
  return (
    <Folder label={String(y.year)} count={inYear.length + wbCount} depth={0} tid={String(y.year)} open={y.year === new Date().getFullYear() || ctx.search}
      actions={<AddMeasure year={y.year} ctx={ctx} existing={ids} />}>
      {() => (
        <>
          {y.measures.map((m) => <MeasureFolder key={m.measure_id} year={y.year} m={m} clients={inYear.filter((c) => c.measure_id === m.measure_id)} ctx={ctx} />)}
          {other.length > 0 && <MeasureFolder year={y.year} m={{ measure_id: "other", name: "Sonstige Maßnahmen", modules: [] }} clients={other} ctx={{ ...ctx, canManage: false }} />}
          <WbFolder year={y.year} classes={wb || []} ctx={ctx} />
        </>
      )}
    </Folder>
  );
}

export default function KundenArchivPage() {
  const [data, setData] = useState(null);
  const [struct, setStruct] = useState(null);
  const [q, setQ] = useState("");
  const load = () => Promise.all([api.get("/clients/archive"), api.get("/ablage/structure")])
    .then(([a, s]) => { setData(a.data); setStruct(s.data); }).catch(err);
  useEffect(() => { load(); }, []);

  const term = q.trim().toLowerCase();
  const clients = useMemo(() => (data?.clients || []).filter((c) => !term || `${c.name} ${(c.coach_names || []).join(" ")}`.toLowerCase().includes(term)), [data, term]);
  const wb = useMemo(() => {
    const out = {};
    Object.entries(struct?.wb || {}).forEach(([y, cls]) => {
      out[y] = cls.map((c) => ({ ...c, participants: c.participants.filter((p) => !term || (p.name || "").toLowerCase().includes(term) || c.name.toLowerCase().includes(term)) }))
        .filter((c) => !term || c.participants.length);
    });
    return out;
  }, [struct, term]);

  const ctx = {
    search: !!term, canRestore: data?.can_restore, canManage: struct?.can_manage, allMeasures: struct?.all_measures || [],
    restore: async (c) => {
      if (!window.confirm(`„${c.name}“ aus der Ablage zurückholen? Der Kunde erscheint wieder in der aktiven AVGS-Übersicht und wird nicht erneut automatisch abgelegt.`)) return;
      try { await api.put(`/clients/${c.client_id}/archived`, { archived: false }); toast.success("Kunde zurückgeholt"); load(); } catch (e) { err(e); }
    },
    addMeasure: async (year, mid) => {
      try { await api.post(`/ablage/years/${year}/measures`, { measure_id: mid }); toast.success("Maßnahmen-Ordner angelegt"); load(); } catch (e) { err(e); }
    },
    removeMeasure: async (year, m) => {
      if (!window.confirm(`Ordner „${m.name}“ in ${year} löschen?`)) return;
      try { await api.delete(`/ablage/years/${year}/measures/${m.measure_id}`); toast.success("Ordner gelöscht"); load(); } catch (e) { err(e); }
    },
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 space-y-8" data-testid="kunden-ablage-page">
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate-500 font-semibold">AVGS · Weiterbildung · Ablage</div>
        <h1 className="text-4xl sm:text-5xl tracking-tight text-slate-900 mt-2">Kunden-Ablage</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">Jahr › Maßnahme › Modul › Monat (Coaching-Start) sowie Weiterbildung › Klasse. AVGS-Kunden landen automatisch hier, sobald das Coaching beendet und jede Rechnung bezahlt ist. Der Ordner fürs neue Jahr wird 30 Tage vorher automatisch angelegt.</p>
      </div>
      <div className="flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 max-w-md focus-within:border-slate-900 transition-colors">
        <Search size={15} className="text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kunde, Coach oder Klasse suchen …" className="flex-1 text-sm outline-none bg-transparent" data-testid="ablage-search-input" />
      </div>
      <div className="border border-slate-200 bg-white" data-testid="ablage-tree">
        {!struct || !data ? <div className="p-8 text-sm text-slate-500">Lädt …</div>
          : struct.years.map((y) => <YearFolder key={y.year} y={y} clients={clients} wb={wb[String(y.year)]} ctx={ctx} />)}
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="ablage-count"><GraduationCap size={13} /> {clients.length} abgelegte AVGS-Kunden</div>
    </div>
  );
}
